import { cn } from '@/internationalization/cn'
import { useT } from '@/internationalization/i18n'

export interface SkeletonProps {
  className?: string
  /** 'text' rounds to a line height; 'block' is a rectangle; 'circle' an avatar. */
  shape?: 'text' | 'block' | 'circle'
}

/**
 * A loading placeholder shaped like the content that will replace it.
 *
 * A skeleton that does not match the real layout causes a visible jump on load,
 * which reads as a bug. If you cannot predict the shape, use a Spinner.
 */
export function Skeleton({ className, shape = 'text' }: SkeletonProps) {
  return (
    <span
      aria-hidden
      className={cn(
        'block animate-pulse bg-surface-muted',
        shape === 'text' && 'h-3.5 rounded',
        shape === 'block' && 'rounded-md',
        shape === 'circle' && 'rounded-full',
        className,
      )}
    />
  )
}

/** N skeleton rows at a fixed height — lists, tables, transcript panes. */
export function SkeletonList({ rows = 5, className }: { rows?: number; className?: string }) {
  const t = useT()
  return (
    <div className={cn('space-y-3', className)} role="status" aria-label={t('common.loading')}>
      {Array.from({ length: rows }).map((_, index) => (
        <div key={index} className="flex items-center gap-3">
          <Skeleton shape="circle" className="h-8 w-8 shrink-0" />
          <div className="flex-1 space-y-1.5">
            <Skeleton className="w-1/3" />
            <Skeleton className="w-2/3" />
          </div>
        </div>
      ))}
    </div>
  )
}
